'use client';

import Link from 'next/link';
import { FC } from 'react';
import * as iconSet from './iconList';
import { cn } from '@/lib/_utils/cn';

export type IconType = keyof typeof iconSet;

export type MenuListType = {
	id: string | number;
	label: string;
	icon: IconType;
	iconActive: IconType;
	iconMobile?: IconType;
	iconMobileActive?: IconType;
	iconInfo?: number | string;
	active?: boolean;
	path?: string;
};

export type MenuProps = {
	selected: string;
	menuList: MenuListType[];
	className?: string;
};

function getIconKey(item: MenuListType, selected = false, mobile = false): keyof typeof iconSet | undefined {
	if (selected) {
		return mobile ? item.iconMobileActive || item.iconActive : item.iconActive;
	} else {
		return mobile ? item.iconMobile || item.icon : item.icon;
	}
}

const GetIcon = ({
	item,
	selected = false,
	mobile = false,
}: {
	item: MenuListType;
	selected?: boolean;
	mobile?: boolean;
}) => {
	const iconKey = getIconKey(item, selected, mobile);

	if (!iconKey) return null;

	const SvgContent = iconSet[iconKey];
	return SvgContent ? <SvgContent /> : null;
};

export const NavItem = ({ item, selected }: { item: MenuListType; selected?: boolean }) => {
	const isActive = selected || item.active;

	return (
		<div className='cursor-pointer text-center transition-opacity hover:opacity-80'>
			<div className={`flex justify-center`}>
				<div className='relative flex'>
					{/* Icon • mobile */}
					<div className='block size-[20px] lg:hidden'>
						<GetIcon item={item} selected={isActive} />
					</div>

					{/* Icon • desktop */}
					<div className='hidden lg:block lg:size-[32px]'>
						<GetIcon item={item} selected={isActive} mobile />
					</div>

					{/* Icon • info */}
					{!!item?.iconInfo && (
						<div className='absolute right-0 top-0 flex size-[18px] -translate-y-[25%] translate-x-[40%] items-center justify-center rounded-full bg-primary-500 text-xs font-semibold -tracking-widest text-dark-950 lg:-translate-y-[25%] lg:translate-x-[-10%]'>
							{item.iconInfo}
						</div>
					)}
				</div>
			</div>

			{/* Label */}
			<div
				className={`mt-[6px] text-sm leading-[1] lg:hidden
					${!selected && !item.active ? 'opacity-50' : ''}
					${selected && !item.active ? 'text-primary-500' : ''}
				`}>
				{item.label}
			</div>
		</div>
	);
};

const Menu: FC<MenuProps> = ({ menuList, selected, className }) => {
	return (
		<>
			{menuList.map((item, itemIndex) => (
				<li key={itemIndex} className={`${cn('list-none text-center', className)}`}>
					{item.path ? (
						<Link href={item.path || ''} className={`block`}>
							<NavItem item={item} selected={selected === item.path} />
						</Link>
					) : (
						<NavItem item={item} selected={selected === item.path} />
					)}
				</li>
			))}
		</>
	);
};

Menu.displayName = 'Menu';
export { Menu };
